import { useEffect, useRef, useState } from 'react';
import {
  player,
  buildPlaybackSrc,
  CITATION_CLICK_MODE,
  type PlaybackState,
} from '../audio/player';
import { Waveform } from './Waveform';

function formatTime(sec: number): string {
  if (!isFinite(sec)) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

export function AudioPlayer() {
  const [state, setState] = useState<PlaybackState>(player.get());
  const audioRef = useRef<HTMLAudioElement>(null);
  const [audioEl, setAudioEl] = useState<HTMLAudioElement | null>(null);
  const spanEndRef = useRef<number | null>(null);

  useEffect(() => player.subscribe(setState), []);

  useEffect(() => {
    setAudioEl(audioRef.current);
  }, [state.current?.episode_id]);

  // Seek + autoplay when a new target is set.
  useEffect(() => {
    const a = audioRef.current;
    if (!a || !state.current) return;
    const wantsSrc = buildPlaybackSrc(state.current);
    if (a.src !== wantsSrc) {
      a.src = wantsSrc;
    }
    a.currentTime = state.position_sec;
    if (state.is_playing) {
      void a.play().catch(() => {/* autoplay blocked; user must click play */});
    }
    spanEndRef.current = (CITATION_CLICK_MODE === 'preview' || CITATION_CLICK_MODE === 'queue')
      ? state.current.end_sec
      : null;
  }, [state.current?.episode_id, state.current?.start_sec]);

  function handleTimeUpdate() {
    const a = audioRef.current;
    if (!a) return;
    if (spanEndRef.current !== null && a.currentTime >= spanEndRef.current) {
      a.pause();
      spanEndRef.current = null;
      if (CITATION_CLICK_MODE === 'queue') player.advance();
      else player.set({ is_playing: false });
      return;
    }
    player.set({ position_sec: a.currentTime });
  }

  function handleEnded() {
    if (CITATION_CLICK_MODE === 'queue') player.advance();
    else player.set({ is_playing: false });
  }

  if (!state.current) return null;

  return (
    <div className="player">
      {state.queue.length > 0 && <QueueStrip state={state} />}
      <div className="player-row">
        <Waveform audio={audioEl} isPlaying={state.is_playing} />
        <div className="player-now">
          <span className={`player-now-marker ${state.is_playing ? '' : 'paused'}`} />
          <span className="player-label">{state.current.label}</span>
        </div>
        <span className="player-time">
          {formatTime(state.position_sec)}
          {state.duration_sec ? ` / ${formatTime(state.duration_sec)}` : ''}
        </span>
      </div>
      <audio
        ref={audioRef}
        onPlay={() => player.set({ is_playing: true })}
        onPause={() => player.set({ is_playing: false })}
        onEnded={handleEnded}
        onLoadedMetadata={(e) => player.set({ duration_sec: e.currentTarget.duration })}
        onTimeUpdate={handleTimeUpdate}
        controls
        preload="none"
        crossOrigin="anonymous"
      />
    </div>
  );
}

function QueueStrip({ state }: { state: PlaybackState }) {
  return (
    <div className="player-queue">
      <span className="player-queue-label">up next ({state.queue.length})</span>
      <ul className="player-queue-list">
        {state.queue.map((q, i) => (
          <li key={`${q.episode_id}-${q.start_sec}-${i}`}>
            <span className="player-queue-item-label">{q.label}</span>
            <button
              className="player-queue-remove"
              title="remove from queue"
              onClick={() => player.removeFromQueue(i)}
            >
              ×
            </button>
          </li>
        ))}
      </ul>
      <button className="player-queue-clear" onClick={() => player.clearQueue()}>
        clear
      </button>
    </div>
  );
}
